import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { formatMixedFractionLatex } from "../../../../utils/fractionLatex";

/*
Typ 2b: Ułamki mieszane – wspólny mianownik + nawias
Postać: A − (C ± E)

Wymagania generatora:
- w A,C,E: 1 albo 2 ułamki MIESZANE (z częścią całkowitą i ułamkową)
- pozostałe: ułamki ZWYKŁE (właściwe), nie liczby całkowite
- mianowniki: 2,3,4,5
- część ułamkowa: właściwa i nieskracalna
- odpowiedź: dowolna równoważna liczbowo postać
*/

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a;
}

function lcm(a, b) {
  return Math.abs((a / gcd(a, b)) * b);
}

function reduceFrac(n, d) {
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}

function addFrac(A, B) {
  const D = lcm(A.d, B.d);
  const n = A.n * (D / A.d) + B.n * (D / B.d);
  return reduceFrac(n, D);
}

function subFrac(A, B) {
  const D = lcm(A.d, B.d);
  const n = A.n * (D / A.d) - B.n * (D / B.d);
  return reduceFrac(n, D);
}

function fracLatex(F) {
  if (F.d === 1) return `${F.n}`;
  return `\\frac{${F.n}}{${F.d}}`;
}

const prettyFracLatex = (fraction) =>
  formatMixedFractionLatex(fraction.n, fraction.d);

/** losuje część ułamkową: n/d, 1<=n<=d-1, gcd(n,d)=1 */
function randomProperIrreduciblePart(denPool) {
  const d = pick(denPool);
  for (let i = 0; i < 80; i++) {
    const n = 1 + Math.floor(Math.random() * (d - 1));
    if (gcd(n, d) === 1) return { n, d };
  }
  return { n: 1, d };
}

/** ułamek mieszany -> ułamek niewłaściwy */
function mixedToImproper({ whole, n, d }) {
  return reduceFrac(whole * d + n, d);
}

/** latex dla liczby (mieszanej albo zwykłej) */
function numberLatex(obj) {
  if (obj.kind === "mixed") {
    return formatMixedFractionLatex(obj.whole * obj.d + obj.n, obj.d);
  }
  // proper
  return `\\frac{${obj.n}}{${obj.d}}`;
}

/** zamiana na ułamek (n/d) do obliczeń */
function toFraction(obj) {
  if (obj.kind === "mixed") return mixedToImproper(obj);
  return reduceFrac(obj.n, obj.d);
}

/** linia skalowania n/d do wspólnego mianownika D */
function scaleLineLatex(n, d, D) {
  const k = D / d;
  if (k === 1) return null; // ✅ nie pokazujemy "·1"
  const nk = n * k;
  return `\\frac{${n}}{${d}}=\\frac{${n}\\cdot${k}}{${d}\\cdot${k}}=\\frac{${nk}}{${D}}`;
}


export function generateTask() {
  const denPool = [2, 3, 4, 5];
  const insideOp = pick(["+", "-"]);

  // 1 albo 2 mieszane, reszta ZWYKŁE (właściwe)
  const mixedCount = pick([1, 2]);
  const flags = [false, false, false];
  const idxs = [0, 1, 2].sort(() => Math.random() - 0.5);
  for (let i = 0; i < mixedCount; i++) flags[idxs[i]] = true;

  function makeNumber(isMixed) {
    if (isMixed) {
      const whole = pick([1, 2]); // część całkowita 1..2
      const part = randomProperIrreduciblePart(denPool); // część ułamkowa zawsze istnieje
      return { kind: "mixed", whole, n: part.n, d: part.d };
    }

    // zwykły ułamek właściwy (nie całkowity)
    const part = randomProperIrreduciblePart(denPool);
    return { kind: "proper", n: part.n, d: part.d };
  }

  const Araw = makeNumber(flags[0]);
  const Craw = makeNumber(flags[1]);
  const Eraw = makeNumber(flags[2]);

  const A = toFraction(Araw);
  const C = toFraction(Craw);
  const E = toFraction(Eraw);

  const inside = insideOp === "+" ? addFrac(C, E) : subFrac(C, E);
  const result = subFrac(A, inside);

  const exprLatex = `${numberLatex(Araw)}-\\left(${numberLatex(Craw)}${insideOp}${numberLatex(Eraw)}\\right)`;

  return {
    type: "FractionMixedCommonDenominatorOpenTask",
    data: {
      Araw,
      Craw,
      Eraw,
      A,
      C,
      E,
      insideOp,
      inside,
      result,
      exprLatex,
    },
    tags: ["dzialania", "ulamki", "ulamki_mieszane", "wspolny_mianownik"],
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        Oblicz wartość:
        <span style={{ marginLeft: 10 }}>
          <InlineMath math={data.exprLatex} />
        </span>
        .
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer || ""}
      onChange={onChange} // obiekt {raw, parsed, ...}
      disabled={disabled}
      label=""
      placeholder="np. -1 3/5, -8/5, 2 1/4..."
      wide={true}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = userAnswer?.parsed;
  if (parsed == null || Number.isNaN(parsed)) return false;

  const expected = data.result.n / data.result.d;
  return Math.abs(parsed - expected) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={prettyFracLatex(data.result)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  const latex = formatLatexPreview(userAnswer.raw, "");
  return <InlineMath math={latex} />;
}

export function renderExplanation({ data }) {
  const { Araw, Craw, Eraw, A, C, E, insideOp, inside, result } = data;

  // pokazujemy zamianę tylko dla tych, które są mieszane
  const showLine = (rawObj, fracObj) => {
    if (rawObj.kind !== "mixed") return null;
    return (
      <BlockMath
        math={`${numberLatex(rawObj)}=\\frac{${fracObj.n}}{${fracObj.d}}`}
      />
    );
  };

  // KROK 1: C (±) E
  const D1 = lcm(C.d, E.d);
  const nC1 = C.n * (D1 / C.d);
  const nE1 = E.n * (D1 / E.d);
  const nInsideAtD1 = insideOp === "+" ? nC1 + nE1 : nC1 - nE1;

  // KROK 2: A - inside
  const D2 = lcm(A.d, inside.d);
  const nA2 = A.n * (D2 / A.d);
  const nInside2 = inside.n * (D2 / inside.d);
  const nResultAtD2 = nA2 - nInside2;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      {(Araw.kind === "mixed" || Craw.kind === "mixed" || Eraw.kind === "mixed") && (
        <>
          <div>
            <strong>Krok 0.</strong> Jeśli mamy ułamek mieszany, zamieniamy go na niewłaściwy:
          </div>
          {showLine(Araw, A)}
          {showLine(Craw, C)}
          {showLine(Eraw, E)}
        </>
      )}

      <div>
        <strong>Krok 1.</strong> Liczymy wyrażenie w nawiasie:
      </div>
      <BlockMath math={`${fracLatex(C)}${insideOp}${fracLatex(E)}`} />

      <div style={{ textAlign: "center", marginTop: 6, opacity: 0.9 }}>
        Wspólny mianownik: <strong>{D1}</strong>
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={scaleLineLatex(C.n, C.d, D1)} />
        {scaleLineLatex(E.n, E.d, D1) && <BlockMath math={scaleLineLatex(E.n, E.d, D1)} />}
        <BlockMath
          math={`\\frac{${nC1}}{${D1}}${insideOp}\\frac{${nE1}}{${D1}}=\\frac{${nInsideAtD1}}{${D1}}=${fracLatex(inside)}`}
        />
      </div>

      <div>
        <strong>Krok 2.</strong> Odejmujemy cały nawias od pierwszego ułamka:
      </div>
      <BlockMath math={`${fracLatex(A)}-${fracLatex(inside)}`} />

      <div style={{ textAlign: "center", marginTop: 6, opacity: 0.9 }}>
        Wspólny mianownik: <strong>{D2}</strong>
      </div>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={scaleLineLatex(A.n, A.d, D2)} />
        <BlockMath math={scaleLineLatex(inside.n, inside.d, D2)} />
        <BlockMath
          math={`\\frac{${nA2}}{${D2}}-\\frac{${nInside2}}{${D2}}=\\frac{${nResultAtD2}}{${D2}}=${fracLatex(result)}`}
        />
      </div>

      <div>
        <strong>Wynik:</strong> <InlineMath math={prettyFracLatex(result)} />
      </div>
    </div>
  );
}
